import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { KINDS, TIER_COLOR } from "@/lib/perform";
import { OctonLogo } from "@/components/ui/OctonLogo";
import { Trophy, X, Maximize2 } from "lucide-react";

// Full-screen whiteboard for the box's TV: today's workout and the live leaderboard.
export default function GymTV() {
  const navigate = useNavigate();
  const { data, error } = useQuery({ queryKey: ["tv"], queryFn: async () => (await api.get("/api/tv/today")).data, refetchInterval: 20000, retry: false });
  const [clock, setClock] = useState(new Date());
  const [board, setBoard] = useState(0);
  useEffect(() => { const id = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(id); }, []);
  useEffect(() => { const id = setInterval(() => setBoard((b) => b + 1), 12000); return () => clearInterval(id); }, []);
  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center text-center p-8">
        <p className="font-heading font-black text-2xl">Gym TV is for club owners and coaches</p>
        <button onClick={() => navigate("/app")} className="mt-4 rounded-full h-10 px-5 bg-acid text-black">Back to the app</button>
      </div>
    );
  }
  const wods = data?.wods || [];
  const boards = wods.flatMap((w) => w.boards.map((b) => ({ ...b, wod: w.title })));
  const cur = boards.length ? boards[board % boards.length] : null;
  const c = data?.club?.color || "#CCFF00";
  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 md:p-10 flex flex-col gap-6" data-testid="gym-tv">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3"><OctonLogo size={44} /><div><p className="font-heading font-black text-3xl tracking-tight">{data?.club?.name || "…"}</p><p className="mono-label" style={{ color: c }}>today's workout</p></div></div>
        <div className="flex items-center gap-4">
          <p className="font-mono2 font-black text-5xl">{clock.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</p>
          <button onClick={() => document.documentElement.requestFullscreen?.()} className="p-2 rounded-full border border-[#343A4A] opacity-60 hover:opacity-100" aria-label="Full screen"><Maximize2 className="w-5 h-5" /></button>
          <button onClick={() => navigate("/app/programming")} className="p-2 rounded-full border border-[#343A4A] opacity-60 hover:opacity-100" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
      </div>
      {wods.length === 0 ? (
        <div className="flex-1 flex items-center justify-center"><p className="font-heading font-black text-4xl text-[#343A4A]">No workout published for today</p></div>
      ) : (
        <div className="flex-1 grid grid-cols-1 xl:grid-cols-5 gap-6">
          <div className="xl:col-span-3 space-y-4">
            {wods.map((w) => (
              <div key={w.id} className="space-y-3">
                <p className="font-heading font-black text-5xl tracking-tight" style={{ color: c }}>{w.title}</p>
                {w.sections.map((s, i) => (
                  <div key={i} className="rounded-2xl border border-[#1E222A] bg-[#0B0D11] p-5" style={{ borderLeft: `6px solid ${(KINDS[s.kind] || KINDS.metcon).c}` }}>
                    <p className="mono-label text-base" style={{ color: (KINDS[s.kind] || KINDS.metcon).c }}>{(KINDS[s.kind] || KINDS.metcon).label}{s.time_cap_min ? ` · cap ${s.time_cap_min} min` : ""}</p>
                    <p className="font-heading font-black text-3xl mt-1">{s.title}</p>
                    {s.description && <p className="text-2xl text-[#D7DCE5] whitespace-pre-line mt-2 leading-snug">{s.description}</p>}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="xl:col-span-2 space-y-4">
            {cur && (
              <div className="rounded-2xl border border-[#1E222A] bg-[#0F1115] p-5">
                <p className="mono-label">whiteboard · {cur.wod}</p>
                <p className="font-heading font-black text-3xl mb-3">{cur.title}</p>
                <div className="space-y-2">
                  {cur.rows.length === 0 && <p className="text-xl text-[#565C6F]">No scores yet</p>}
                  {cur.rows.map((r) => (
                    <div key={r.rank} className="flex items-center gap-3 text-2xl">
                      <span className="w-10 font-black" style={{ color: r.rank <= 3 ? c : "#565C6F" }}>{r.rank}</span>
                      <span className="flex-1 truncate">{r.name}</span>
                      <span className="mono-label text-sm" style={{ color: TIER_COLOR[r.tier] }}>{r.tier}</span>
                      <span className="font-mono2 font-black w-40 text-right">{r.display}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data?.prs?.length > 0 && (
              <div className="rounded-2xl border border-[#FFD60A]/40 bg-[#FFD60A]/5 p-5">
                <p className="font-heading font-black text-2xl flex items-center gap-2"><Trophy className="w-6 h-6 text-[#FFD60A]" /> PRs this week</p>
                <div className="mt-2 space-y-1.5">
                  {data.prs.map((p, i) => <p key={i} className="text-xl"><span className="text-[#FFD60A] font-bold">{p.name}</span> {p.movement} <span className="text-[#B7BECB]">{p.detail}</span></p>)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
